import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Loader, Spinner } from '../../components/Common/Loader';
import { CustomDropdown } from '../../components/Common/CustomDropdown';
import { getProductDropdowns, createProduct } from '../../api/products';
import { getCategories, getBrands, getSizes, getUnits, getPackagings } from '../../api/productMasters';

interface ProductFormProps {
  onSubmit: (data: any) => void;
  onCancel: () => void;
  initialData?: any;
  isEditMode?: boolean;
  isLoading?: boolean;
}

const renderTextLabel = (val: any, fallback: string = ''): string => {
  if (val == null) return fallback;
  if (typeof val === 'object') {
    return String(val.name || val.category_name || val.brand_name || val.packagingType || val.title || val.sizeValue || fallback);
  }
  return String(val);
};

export const ProductForm: React.FC<ProductFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  isEditMode = false,
  isLoading = false,
}) => {
  const skuFileRef = useRef<HTMLInputElement>(null);
  const galleryFilesRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: '',
    mrp: '',
    gst: '0',
    hsnCode: '',
    price: '',
    unitsPerCase: '',
    abbreviation: '',
    skuCode: '',
    standardCase: '',
    expiryTimeline: '',
    description: '',
    unitCase: '1',
    subBrand: '',
    packaging: '',
    size: '',
    subcategory: '',
    brand: '',
    category: '',
    promoSku: false,
    forDevice: true,
    focusSku: false,
  });

  const [skuImageFile, setSkuImageFile] = useState<File | null>(null);
  const [skuImagePreview, setSkuImagePreview] = useState<string | null>(null);

  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [galleryPreviews, setGalleryPreviews] = useState<string[]>([]);

  const [submitting, setSubmitting] = useState<boolean>(false);

  // Dropdown States
  const [dropdowns, setDropdowns] = useState<{
    categories: any[];
    brands: any[];
    packagings: any[];
    units: any[];
    sizes: any[];
  }>({
    categories: [],
    brands: [],
    packagings: [],
    units: [],
    sizes: [],
  });

  // Fetch dropdown data from API on mount
  useEffect(() => {
    let isMounted = true;
    const loadDropdownData = async () => {
      try {
        const res = await getProductDropdowns();
        if (isMounted && res) {
          const raw = res.data || res || {};
          const categories = Array.isArray(raw.categories) ? raw.categories : (Array.isArray(raw.category) ? raw.category : []);
          const brands = Array.isArray(raw.brands) ? raw.brands : (Array.isArray(raw.brand) ? raw.brand : []);
          const packagings = Array.isArray(raw.packagings) ? raw.packagings : (Array.isArray(raw.crates) ? raw.crates : (Array.isArray(raw.packaging) ? raw.packaging : []));
          const units = Array.isArray(raw.units) ? raw.units : (Array.isArray(raw.unit) ? raw.unit : []);
          const sizes = Array.isArray(raw.sizes) ? raw.sizes : (Array.isArray(raw.size) ? raw.size : []);

          if (categories.length > 0 || brands.length > 0 || packagings.length > 0 || units.length > 0 || sizes.length > 0) {
            setDropdowns({ categories, brands, packagings, units, sizes });
            return;
          }
        }
      } catch (err) {
        console.warn('getProductDropdowns failed, attempting master APIs fallback...', err);
      }

      // Fallback: Fetch from individual master APIs
      try {
        const [catsRes, brandsRes, pkgsRes, unitsRes, sizesRes] = await Promise.allSettled([
          getCategories(),
          getBrands(),
          getPackagings(),
          getUnits(),
          getSizes(),
        ]);

        if (isMounted) {
          setDropdowns({
            categories: catsRes.status === 'fulfilled' && catsRes.value?.data ? (Array.isArray(catsRes.value.data) ? catsRes.value.data : []) : [],
            brands: brandsRes.status === 'fulfilled' && brandsRes.value?.data ? (Array.isArray(brandsRes.value.data) ? brandsRes.value.data : []) : [],
            packagings: pkgsRes.status === 'fulfilled' && pkgsRes.value?.data ? (Array.isArray(pkgsRes.value.data) ? pkgsRes.value.data : []) : [],
            units: unitsRes.status === 'fulfilled' && unitsRes.value?.data ? (Array.isArray(unitsRes.value.data) ? unitsRes.value.data : []) : [],
            sizes: sizesRes.status === 'fulfilled' && sizesRes.value?.data ? (Array.isArray(sizesRes.value.data) ? sizesRes.value.data : []) : [],
          });
        }
      } catch (fallbackErr) {
        console.error('Failed to load dropdown options from master APIs:', fallbackErr);
      }
    };

    loadDropdownData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Prefill Form Data in Edit Mode
  useEffect(() => {
    if (!initialData) return;

    // Robust unwrapping of initialData
    let data = initialData;
    if (data?.data) data = data.data;
    if (Array.isArray(data) && data.length > 0) data = data[0];
    if (data?.product) data = data.product;
    if (data?.item) data = data.item;

    if (!data || typeof data !== 'object') return;

    // Resolve Category
    let categoryVal = data.category_id != null ? String(data.category_id) : '';
    if (!categoryVal && data.category_name && dropdowns?.categories) {
      const match = dropdowns.categories.find(
        (c: any) => String(c.name || c.category_name).toLowerCase() === String(data.category_name).toLowerCase()
      );
      if (match) categoryVal = String(match.id);
    }
    if (!categoryVal && data.category_name) categoryVal = String(data.category_name);
    if (!categoryVal && data.category) {
      categoryVal = typeof data.category === 'object' ? String(data.category.id || data.category.name || '') : String(data.category);
    }

    // Resolve Brand
    let brandVal = data.brand_id != null ? String(data.brand_id) : '';
    if (!brandVal && data.brand && dropdowns?.brands) {
      const match = dropdowns.brands.find(
        (b: any) => String(b.name || b.brand_name).toLowerCase() === String(data.brand).toLowerCase()
      );
      if (match) brandVal = String(match.id);
    }
    if (!brandVal && data.brand_name) brandVal = String(data.brand_name);
    if (!brandVal && data.brand) {
      brandVal = typeof data.brand === 'object' ? String(data.brand.id || data.brand.name || '') : String(data.brand);
    }

    // Resolve Size
    let sizeVal = data.size_id != null ? String(data.size_id) : '';
    if (!sizeVal && data.size && dropdowns?.sizes) {
      const match = dropdowns.sizes.find(
        (s: any) =>
          String(s.name || '').toLowerCase().includes(String(data.size).toLowerCase()) ||
          String(s.sizeValue || '').toLowerCase() === String(data.size).toLowerCase()
      );
      if (match) sizeVal = String(match.id);
    }
    if (!sizeVal && data.size) {
      sizeVal = typeof data.size === 'object' ? String(data.size.id || data.size.name || '') : String(data.size);
    }
    if (!sizeVal && data.variant) sizeVal = String(data.variant);

    // Resolve Packaging
    let packagingVal = data.crate_id != null ? String(data.crate_id) : (data.packaging_id != null ? String(data.packaging_id) : '');
    if (!packagingVal && data.packaging && dropdowns?.packagings) {
      const match = dropdowns.packagings.find(
        (p: any) => String(p.name || p.packagingType).toLowerCase() === String(data.packaging).toLowerCase()
      );
      if (match) packagingVal = String(match.id);
    }
    if (!packagingVal && data.packaging) {
      packagingVal = typeof data.packaging === 'object' ? String(data.packaging.id || data.packaging.name || '') : String(data.packaging);
    }

    // Resolve Units Per Case
    const unitsPerCaseVal =
      data.units_per_case != null && data.units_per_case !== ''
        ? String(data.units_per_case)
        : data.crate_qty != null && data.crate_qty !== ''
        ? String(data.crate_qty)
        : data.unitsPerCase != null && data.unitsPerCase !== ''
        ? String(data.unitsPerCase)
        : data.units_per_crate != null && data.units_per_crate !== ''
        ? String(data.units_per_crate)
        : data.unitsPerCrate != null && data.unitsPerCrate !== ''
        ? String(data.unitsPerCrate)
        : '';

    // MRP Resolution
    const mrpVal =
      data.mrp != null && data.mrp !== ''
        ? String(data.mrp)
        : data.MRP != null && data.MRP !== ''
        ? String(data.MRP)
        : data.product_mrp != null && data.product_mrp !== ''
        ? String(data.product_mrp)
        : data.maxRetailPrice != null && data.maxRetailPrice !== ''
        ? String(data.maxRetailPrice)
        : '';

    // Price Resolution
    const priceVal =
      data.price != null && data.price !== ''
        ? String(data.price)
        : data.Price != null && data.Price !== ''
        ? String(data.Price)
        : data.unit_price != null && data.unit_price !== ''
        ? String(data.unit_price)
        : data.unitPrice != null && data.unitPrice !== ''
        ? String(data.unitPrice)
        : data.selling_price != null && data.selling_price !== ''
        ? String(data.selling_price)
        : '';

    // GST Resolution
    const gstVal =
      data.gst_percent != null && data.gst_percent !== ''
        ? String(data.gst_percent)
        : data.gstPercentage != null && data.gstPercentage !== ''
        ? String(data.gstPercentage)
        : data.gst != null && data.gst !== ''
        ? String(data.gst)
        : '';

    // Safe Non-Destructive Form Data Update
    setFormData((prev) => ({
      name: data.name || data.product_name || data.displayName || prev.name || '',
      mrp: mrpVal !== '' ? mrpVal : prev.mrp,
      gst: gstVal !== '' ? gstVal : prev.gst,
      hsnCode: data.hsn_code || data.hsnCode || prev.hsnCode || '',
      price: priceVal !== '' ? priceVal : prev.price,
      unitsPerCase: unitsPerCaseVal !== '' ? unitsPerCaseVal : prev.unitsPerCase,
      abbreviation: data.abbreviation || prev.abbreviation || '',
      skuCode: data.sku_code || data.skuCode || prev.skuCode || '',
      standardCase: data.standard_case || data.standardCase || prev.standardCase || '',
      expiryTimeline: data.expiry_timeline || data.expiryTimeline || prev.expiryTimeline || '',
      description: data.description || prev.description || '',
      unitCase: data.unit_id != null ? String(data.unit_id) : (data.unit_case != null ? String(data.unit_case) : prev.unitCase),
      subBrand: data.sub_brand || data.subBrand || prev.subBrand || '',
      packaging: packagingVal !== '' ? packagingVal : prev.packaging,
      size: sizeVal !== '' ? sizeVal : prev.size,
      subcategory: data.sub_category || data.subcategory || prev.subcategory || '',
      brand: brandVal !== '' ? brandVal : prev.brand,
      category: categoryVal !== '' ? categoryVal : prev.category,
      promoSku: Boolean(data.is_promo_sku ?? data.promoSku ?? prev.promoSku),
      forDevice: Boolean(data.for_device ?? data.forDevice ?? prev.forDevice),
      focusSku: Boolean(data.is_focus_sku ?? data.focusSku ?? prev.focusSku),
    }));

    const mediaObj = Array.isArray(data.media) && data.media.length > 0
      ? data.media[0]
      : (typeof data.media === 'object' && data.media !== null ? data.media : null);

    const mainLogoImg =
      mediaObj?.logoImg ||
      data.logoImg ||
      data['main-img'] ||
      null;

    if (mainLogoImg) {
      setSkuImagePreview(mainLogoImg);
    }

    const rawSlider =
      mediaObj?.sliderImg ||
      data.sliderImg ||
      data['slider-img'];

    if (Array.isArray(rawSlider) && rawSlider.length > 0) {
      setGalleryPreviews(rawSlider.map((img: any) => (typeof img === 'string' ? img : img.url || '')).filter(Boolean));
    }
  }, [initialData, dropdowns]);

  // Single SKU Image Handler
  const handleSkuFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSkuImageFile(file);
      setSkuImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveSkuImage = () => {
    setSkuImageFile(null);
    setSkuImagePreview(null);
    if (skuFileRef.current) {
      skuFileRef.current.value = '';
    }
  };

  // Multiple Images Handler (Image A, Image B, Image C...)
  const handleGalleryFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      setGalleryFiles((prev) => [...prev, ...selectedFiles]);
      const newPreviews = selectedFiles.map((file) => URL.createObjectURL(file));
      setGalleryPreviews((prev) => [...prev, ...newPreviews]);
    }
  };

  const handleRemoveGalleryImage = (index: number) => {
    setGalleryFiles((prev) => prev.filter((_, i) => i !== index));
    setGalleryPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Product Name is required.');
      return;
    }

    const payload: any = {
      name: formData.name,
      displayName: formData.name,
      mrp: formData.mrp || '0',
      price: formData.price || '0',
      gstPercentage: formData.gst || '0',
      hsnCode: formData.hsnCode || '',
      category_id: formData.category || '1',
      brand_id: formData.brand || '1',
      size_id: formData.size || '1',
      unit_id: formData.unitCase || '1',
      crate_id: formData.packaging || '1',
      crate_qty: formData.unitsPerCase || '12',
      stockQty: '500',
      description: formData.description || '',
    };

    if (skuImageFile) {
      payload['main-img'] = skuImageFile;
    } else if (skuImagePreview) {
      payload['main-img'] = skuImagePreview;
    }

    if (galleryFiles.length > 0) {
      payload['slider-img'] = galleryFiles;
    } else if (galleryPreviews.length > 0) {
      payload['slider-img'] = galleryPreviews;
    }

    try {
      setSubmitting(true);
      if (!isEditMode) {
        await createProduct(payload);
      }
      onSubmit(payload);
    } catch (err: any) {
      console.error('Error saving product:', err);
      alert(err.message || 'Failed to save product. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button type="button"
          onClick={onCancel}
          className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
          title="Go Back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight m-0">
          {isEditMode ? 'Edit Product' : 'Add Product'}
        </h1>
      </div>

      {isLoading ? (
        <div className="bg-white p-12 rounded-2xl">
          <Loader text="Loading..." />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-2xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {/* Row 1 */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Name*</label>
              <input 
                type="text" 
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full bg-[#ECECF2]/50 border border-slate-300 rounded-xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">MRP*</label>
              <input 
                type="text" 
                required
                value={formData.mrp}
                onChange={(e) => setFormData({...formData, mrp: e.target.value})}
                className="w-full bg-[#ECECF2]/50 border border-slate-300 rounded-xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>

            {/* Row 2 */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">GST(%)</label>
              <input 
                type="text" 
                value={formData.gst}
                onChange={(e) => setFormData({...formData, gst: e.target.value})}
                className="w-full bg-[#ECECF2]/50 border border-slate-300 rounded-xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">HSN Code</label>
              <input 
                type="text" 
                value={formData.hsnCode}
                onChange={(e) => setFormData({...formData, hsnCode: e.target.value})}
                className="w-full bg-[#ECECF2]/50 border border-slate-300 rounded-xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>

            {/* Row 3 */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Price*</label>
              <input 
                type="text" 
                required
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                className="w-full bg-[#ECECF2]/50 border border-slate-300 rounded-xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>

            {/* Row 4 */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">SKU/SAP Code</label>
              <input 
                type="text" 
                value={formData.skuCode}
                onChange={(e) => setFormData({...formData, skuCode: e.target.value})}
                className="w-full bg-[#ECECF2]/50 border border-slate-300 rounded-xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>

            {/* Row 5 */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Validity</label>
              <input 
                type="text" 
                value={formData.expiryTimeline}
                onChange={(e) => setFormData({...formData, expiryTimeline: e.target.value})}
                className="w-full bg-[#ECECF2]/50 border border-slate-300 rounded-xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>

            {/* Row 6 */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Description</label>
              <input 
                type="text" 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full bg-[#ECECF2]/50 border border-slate-300 rounded-xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>
          </div>

          {/* Dropdown Rows - 3 columns */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
            {/* Category */}
            <div>
              <CustomDropdown
                label="CATEGORY *"
                placeholder="Please Select"
                options={[
                  'Please Select',
                  ...(dropdowns?.categories?.length > 0
                    ? dropdowns.categories.map((c: any) =>
                        renderTextLabel(c.name || c.category_name || c.title, `Category ${c.id}`)
                      )
                    : ['Milk', 'Beverages', 'Bucket Curd', 'Butter Milk', 'Flavored Dahi', 'LASSI', 'Dahi / Cheese', 'PANEER', 'Pouch Curd']),
                ]}
                value={
                  dropdowns?.categories?.find((c: any) => String(c.id) === String(formData.category))?.name ||
                  renderTextLabel(initialData?.category_name || initialData?.category, formData.category || '') ||
                  'Please Select'
                }
                onChange={(val: string) => {
                  const matched = dropdowns?.categories?.find(
                    (c: any) => renderTextLabel(c.name || c.category_name || c.title, '') === val || String(c.id) === String(val)
                  );
                  setFormData({ ...formData, category: matched ? String(matched.id) : (val === 'Please Select' ? '' : val) });
                }}
                searchable={true}
              />
            </div>

            {/* Brand */}
            <div>
              <CustomDropdown
                label="Brand"
                placeholder="Please Select"
                options={[
                  'Please Select',
                  ...(dropdowns?.brands?.length > 0
                    ? dropdowns.brands.map((b: any) => renderTextLabel(b.name || b.brand_name, `Brand ${b.id}`))
                    : ['Gowardhan']),
                ]}
                value={
                  dropdowns?.brands?.find((b: any) => String(b.id) === String(formData.brand))?.name ||
                  renderTextLabel(initialData?.brand, formData.brand || '') ||
                  'Please Select'
                }
                onChange={(val: string) => {
                  const matched = dropdowns?.brands?.find(
                    (b: any) => renderTextLabel(b.name || b.brand_name, '') === val || String(b.id) === String(val)
                  );
                  setFormData({ ...formData, brand: matched ? String(matched.id) : (val === 'Please Select' ? '' : val) });
                }}
                searchable={true}
              />
            </div>

            {/* Packaging / Crate */}
            <div>
              <CustomDropdown
                label="Packaging"
                placeholder="Please Select"
                options={[
                  'Please Select',
                  ...(dropdowns?.packagings?.length > 0
                    ? dropdowns.packagings.map((p: any) => renderTextLabel(p.name || p.packagingType, `Packaging ${p.id}`))
                    : ['Pouch', 'Tetra', 'Cup', 'Bottle', 'Bucket']),
                ]}
                value={
                  dropdowns?.packagings?.find((p: any) => String(p.id) === String(formData.packaging))?.name ||
                  renderTextLabel(initialData?.packaging, formData.packaging || '') ||
                  'Please Select'
                }
                onChange={(val: string) => {
                  const matched = dropdowns?.packagings?.find(
                    (p: any) => renderTextLabel(p.name || p.packagingType, '') === val || String(p.id) === String(val)
                  );
                  setFormData({ ...formData, packaging: matched ? String(matched.id) : (val === 'Please Select' ? '' : val) });
                }}
                searchable={true}
              />
            </div>

            {/* Units Per Case */}
            <div>
              <CustomDropdown
                label="Units *"
                placeholder="Please Select"
                options={['Please Select', '1', '6', '10', '12', '18', '20', '24', '30', '48']}
                value={String(formData.unitsPerCase || 'Please Select')}
                onChange={(val: string) => setFormData({ ...formData, unitsPerCase: val === 'Please Select' ? '' : val })}
                searchable={false}
              />
            </div>

            {/* Size */}
            <div>
              <CustomDropdown
                label="Size"
                placeholder="Please Select"
                options={[
                  'Please Select',
                  ...(dropdowns?.sizes?.length > 0
                    ? dropdowns.sizes.map((s: any) => renderTextLabel(s.name || (s.sizeValue ? `${s.sizeValue} ${s.unit?.symbol || ''}` : null), `Size ${s.id}`))
                    : ['100g', '200g', '180 Ml', '200 Ml', '500 Ml', '1 Ltr', '5 Ltrs', '5 Kg']),
                ]}
                value={
                  dropdowns?.sizes?.find((s: any) => String(s.id) === String(formData.size))?.name ||
                  renderTextLabel(initialData?.size, formData.size || '') ||
                  'Please Select'
                }
                onChange={(val: string) => {
                  const matched = dropdowns?.sizes?.find(
                    (s: any) => renderTextLabel(s.name || (s.sizeValue ? `${s.sizeValue} ${s.unit?.symbol || ''}` : null), '') === val || String(s.id) === String(val)
                  );
                  setFormData({ ...formData, size: matched ? String(matched.id) : (val === 'Please Select' ? '' : val) });
                }}
                searchable={true}
              />
            </div>
          </div>

          {/* Uploads Section */}
          <div className="space-y-6 pt-4 border-t border-slate-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* 1. Single SKU Image Upload */}
              <div>
                <label className="block text-slate-800 font-bold text-xs mb-1.5">SKU Image (Single Image - main-img)</label>
                
                <input 
                  type="file" 
                  ref={skuFileRef}
                  accept="image/png, image/jpeg, image/jpg, image/webp"
                  onChange={handleSkuFileChange}
                  className="hidden"
                />

                <div className="flex items-center border border-slate-300 rounded-xl overflow-hidden bg-white max-w-md">
                  <button 
                    type="button" 
                    onClick={() => skuFileRef.current?.click()}
                    className="go_erp-bg text-white text-xs font-bold py-2.5 px-4 cursor-pointer hover:opacity-90 transition-opacity"
                  >
                    Upload
                  </button>
                  <input 
                    type="text" 
                    readOnly
                    placeholder="Choose single image (.png, .jpg, .webp)" 
                    value={skuImageFile ? skuImageFile.name : (skuImagePreview ? '1 Image Selected' : '')}
                    onClick={() => skuFileRef.current?.click()}
                    className="flex-1 bg-transparent px-3 text-xs text-slate-700 outline-none cursor-pointer font-semibold truncate"
                  />
                  <button 
                    type="button" 
                    onClick={() => skuFileRef.current?.click()}
                    className="bg-[#FFF8E1] border-l border-slate-300 text-slate-700 hover:bg-[#FFF3CD] text-xs font-bold py-2.5 px-4 cursor-pointer transition-colors"
                  >
                    Browse
                  </button>
                </div>

                {/* SKU Image Single Preview */}
                {skuImagePreview && (
                  <div className="mt-3 relative w-24 h-24 rounded-xl border border-slate-200 overflow-hidden group shadow-sm bg-slate-50">
                    <img 
                      src={skuImagePreview} 
                      alt="SKU Preview" 
                      className="w-full h-full object-cover"
                    />
                    <button 
                      type="button"
                      onClick={handleRemoveSkuImage}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] shadow-md hover:bg-red-700 cursor-pointer"
                      title="Remove Image"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>

              {/* 2. Multiple Product Images Upload */}
              <div>
                <label className="block text-slate-800 font-bold text-xs mb-1.5">Multiple Slider Images (slider-img)</label>
                
                <input 
                  type="file" 
                  ref={galleryFilesRef}
                  accept="image/png, image/jpeg, image/jpg, image/webp"
                  multiple
                  onChange={handleGalleryFilesChange}
                  className="hidden"
                />

                <div className="flex items-center gap-3">
                  <button 
                    type="button" 
                    onClick={() => galleryFilesRef.current?.click()}
                    className="go_erp-bg text-white text-xs font-bold py-2.5 px-5 rounded-xl flex items-center gap-2 cursor-pointer shadow-sm hover:opacity-90 transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    Select Multiple Images
                  </button>

                  <span className="text-xs text-slate-500 font-semibold">
                    {galleryPreviews?.length > 0 ? `${galleryPreviews.length} image(s) selected` : 'No images added yet'}
                  </span>
                </div>

                {/* Multiple Images Preview Gallery */}
                {galleryPreviews?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-3">
                    {galleryPreviews.map((previewUrl, idx) => (
                      <div key={idx} className="relative w-20 h-20 rounded-xl border border-slate-200 overflow-hidden shadow-sm bg-slate-50 group">
                        <img 
                          src={previewUrl} 
                          alt={`Image ${idx + 1}`} 
                          className="w-full h-full object-cover"
                        />
                        <span className="absolute bottom-0 inset-x-0 bg-slate-900/60 text-white text-[9px] font-bold text-center py-0.5 truncate px-1">
                          Img {idx + 1}
                        </span>
                        <button 
                          type="button"
                          onClick={() => handleRemoveGalleryImage(idx)}
                          className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] shadow-md hover:bg-red-700 cursor-pointer"
                          title="Remove Image"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          </div>

          {/* Add / Update Button */}
          <div className="flex justify-center pt-6">
            <button 
              type="submit" 
              disabled={submitting}
              className="bg-[#DCA10D] hover:bg-[#c28c00] text-white font-bold py-3 px-16 rounded-xl text-sm transition-all cursor-pointer shadow-md hover:shadow-[#DCA10D]/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting && <Spinner size="sm" color="text-white" />}
              {submitting ? 'Submitting...' : (isEditMode ? 'Update Product' : 'Add')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
export default ProductForm;
